/**
 * FILTRO CLIENTE SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con secciones anteriores:
 * - Fondo: #f7f9fb
 * - Colores: #0b1220, #6b7280, emerald/red solo en iconos
 * - Sin gradientes decorativos
 * - Borders sutiles (1px)
 * - Animaciones reveal con Intersection Observer
 * - Toggle con ChevronDown de Lucide
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  CheckCircle2, 
  XCircle,
  TrendingUp,
  AlertTriangle,
  Truck,
  FileText,
  Target,
  Calendar,
  ChevronDown,
  Users,
  Zap
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── FilterCard Component ─────────────────────────────────────────────────────
const FilterCard = ({ icon, titulo, descripcion, variant, delay }) => {
  const ref = useReveal(delay);
  
  const styles = {
    ideal: {
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    noIdeal: {
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
    }
  };

  const s = styles[variant];

  return (
    <div
      ref={ref}
      className="flex gap-3.5 p-4 bg-white rounded-lg border border-black/[0.08] shadow-[0_4px_12px_rgba(15,23,42,0.03)] hover:shadow-[0_8px_20px_rgba(15,23,42,0.06)] hover:border-black/[0.12] transition-all duration-300"
    >
      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg ${s.iconBg} flex items-center justify-center shrink-0`}>
        {React.cloneElement(icon, { 
          size: 18, 
          strokeWidth: 2, 
          className: s.iconColor 
        })}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1">
        <h4 className="text-[14px] font-semibold text-[#0b1220] leading-tight">
          {titulo}
        </h4>
        <p className="text-[13px] text-[#6b7280] leading-[1.65]">
          {descripcion}
        </p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FiltroCliente = () => {
  const [mostrarAyuda, setMostrarAyuda] = useState(false);

  const headerRef = useReveal(0);
  const columnasRef = useReveal(0);
  const casosRef = useReveal(0);
  const checklistRef = useReveal(0);

  const scrollToForm = () => {
    document.getElementById('formulario-contacto')?.scrollIntoView({ behavior: 'smooth' });
  };

  const clienteIdeal = [
    {
      icon: <Truck />,
      titulo: "Tienes entre 2 y 10 vehículos de carga",
      descripcion: "Es el rango perfecto. Tienes suficiente operación como para que el control sea crítico, pero no tan grande que necesites un ERP empresarial."
    },
    {
      icon: <FileText />,
      titulo: "Operas de manera formal",
      descripcion: "Emites facturas, tienes choferes en planilla o con recibos, pagas impuestos. Necesitas llevar control ordenado."
    },
    {
      icon: <TrendingUp />,
      titulo: "Quieres crecer de manera rentable",
      descripcion: "No solo quieres más viajes, quieres que cada viaje te deje ganancia real. Buscas decisiones basadas en datos, no intuición."
    },
    {
      icon: <AlertTriangle />,
      titulo: "Sientes que trabajas mucho pero ganas poco",
      descripcion: "Tus camiones no paran, pero al final del mes apenas queda. Sospechas que hay viajes o clientes que te hacen perder dinero."
    },
    {
      icon: <Target />,
      titulo: "Quieres tomar mejores decisiones",
      descripcion: "¿Comprar otro camión? ¿Subir tarifas? ¿Dejar un cliente? Quieres decidir con números reales, no con 'feeling'."
    }
  ];

  const clienteNoIdeal = [
    {
      icon: <XCircle />,
      titulo: "Solo tienes 1 camión",
      descripcion: "El sistema está pensado para quien tiene varios vehículos y necesita comparar rentabilidad entre ellos. Con 1 solo camión, Excel puede ser suficiente."
    },
    {
      icon: <XCircle />,
      titulo: "Tienes más de 15 vehículos",
      descripcion: "Probablemente necesitas un ERP completo con módulos de RRHH, almacén, facturación electrónica integrada. Nuestro sistema es específico para empresas medianas."
    },
    {
      icon: <XCircle />,
      titulo: "Operas informalmente",
      descripcion: "Si no emites facturas ni llevas registros ordenados, implementar un sistema de control no tiene sentido todavía. Primero formaliza tu operación."
    },
    {
      icon: <XCircle />,
      titulo: "Buscas una solución 100% gratuita",
      descripcion: "Somos honestos: esto es un servicio con valor y tiene un costo. Si no estás dispuesto a invertir en control, este no es el momento."
    }
  ];

  const casosEspeciales = [
    {
      pregunta: "Tengo 11-12 vehículos, ¿me sirve?",
      respuesta: "Sí, perfectamente. El límite de 10 es una guía, no una regla estricta. Entre 10-15 vehículos todavía funciona muy bien. Agenda el diagnóstico y conversamos tu caso específico."
    },
    {
      pregunta: "Estoy empezando a formalizarme, ¿puedo usar el sistema?",
      respuesta: "Sí, de hecho es el mejor momento. Si ya tienes 2-3 vehículos y estás comenzando a facturar, el sistema te ayuda a crecer ordenadamente desde el inicio."
    },
    {
      pregunta: "Hago transporte interprovincial y local, ¿funciona para ambos?",
      respuesta: "Sí, el sistema funciona para cualquier tipo de carga: interprovincial, local, urbano, refrigerado. Lo importante es que registres los costos específicos de cada tipo de viaje."
    },
    {
      pregunta: "Tengo algunos camiones propios y otros alquilados, ¿puedo controlarlo?",
      respuesta: "Perfecto, el sistema está diseñado para eso. Puedes registrar tanto vehículos propios como alquilados, cada uno con su estructura de costos diferente."
    }
  ];

  const checklistItems = [
    "Tienes 2-10 vehículos",
    "Emites facturas",
    "Tienes choferes fijos o frecuentes",
    "Controlas combustible y peajes",
    "Quieres saber rentabilidad real",
    "Puedes invertir en control"
  ];

  return (
    <section
      id="filtro-cliente"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#f7f9fb' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] flex flex-col gap-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-200 w-fit">
            <Target size={14} strokeWidth={2} className="text-amber-600" />
            <span className="text-[13px] font-medium text-amber-700">Honestidad primero</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Este sistema{' '}
            <span className="text-amber-600">NO es para todos</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            Somos honestos: hay empresas para las que este sistema es{' '}
            <strong className="text-[#0b1220] font-semibold">perfecto</strong>, y otras para las que{' '}
            <strong className="text-[#0b1220] font-semibold">no tiene sentido</strong>. Revisa si encajas en el perfil ideal.
          </p>
        </header>

        {/* ── Grid Sí/No ── */}
        <div
          ref={columnasRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* COLUMNA IDEAL */}
          <div className="flex flex-col gap-5 bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden">
            {/* Header */}
            <div className="flex items-start gap-3 p-6 border-b border-black/[0.06] bg-emerald-50/50">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <CheckCircle2 size={20} strokeWidth={2} className="text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-bold text-[#0b1220] leading-tight mb-1">
                  ✓ Este sistema SÍ es para ti si:
                </h3>
                <p className="text-[13px] text-[#6b7280]">
                  Estos son los clientes que obtienen mejores resultados
                </p>
              </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col gap-3 px-6">
              {clienteIdeal.map((item, i) => (
                <FilterCard
                  key={i}
                  {...item}
                  variant="ideal"
                  delay={i * 40}
                />
              ))}
            </div>

            {/* CTA */}
            <div className="p-6 bg-blue-50 border-t border-blue-100">
              <p className="text-[14px] font-semibold text-[#0b1220] mb-3 text-center">
                ¿Te identificas con este perfil?
              </p>
              <button
                onClick={scrollToForm}
                className="w-full flex items-center justify-center gap-2 bg-[#0b63c6] hover:bg-[#0957b0] text-white text-[14px] font-semibold px-5 py-3 rounded-lg transition-colors duration-200"
              >
                <Calendar size={18} strokeWidth={2} />
                Sí, Agendar Diagnóstico Gratuito
              </button>
            </div>
          </div>

          {/* COLUMNA NO IDEAL */}
          <div className="flex flex-col gap-5 bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden">
            {/* Header */}
            <div className="flex items-start gap-3 p-6 border-b border-black/[0.06] bg-red-50/50">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                <XCircle size={20} strokeWidth={2} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-bold text-[#0b1220] leading-tight mb-1">
                  ✗ Este sistema NO es para ti si:
                </h3>
                <p className="text-[13px] text-[#6b7280]">
                  Queremos ser honestos desde el inicio
                </p>
              </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col gap-3 px-6">
              {clienteNoIdeal.map((item, i) => (
                <FilterCard
                  key={i}
                  {...item}
                  variant="noIdeal"
                  delay={i * 40}
                />
              ))}
            </div>

            {/* Nota */}
            <div className="flex items-start gap-3 p-6 bg-amber-50 border-t border-amber-100">
              <AlertTriangle size={16} strokeWidth={2} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#6b7280] leading-[1.65]">
                Si no encajas en el perfil ideal ahora,{' '}
                <strong className="text-[#0b1220] font-semibold">no significa que nunca</strong>. 
                Quizás en unos meses, cuando crezcas o formalices, sea el momento perfecto.
              </p>
            </div>
          </div>
        </div>

        {/* ── Casos Especiales (FAQ) ── */}
        <div
          ref={casosRef}
          className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden"
        >
          {/* Header colapsable */}
          <button
            onClick={() => setMostrarAyuda(!mostrarAyuda)}
            className="w-full flex items-center gap-3 p-6 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-colors duration-200 border-b border-black/[0.06]"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Target size={20} strokeWidth={2} className="text-[#0b63c6]" />
            </div>
            <h3 className="flex-1 text-[16px] font-semibold text-[#0b1220] text-left">
              ¿No estás seguro si encajas?
            </h3>
            <ChevronDown
              size={20}
              strokeWidth={2}
              className={`text-[#6b7280] transition-transform duration-300 ${
                mostrarAyuda ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Contenido colapsable */}
          {mostrarAyuda && (
            <div className="flex flex-col gap-5 p-6">
              {casosEspeciales.map((caso, i) => (
                <div
                  key={i}
                  className="pb-5 border-b border-black/[0.06] last:border-b-0 last:pb-0"
                >
                  <h4 className="text-[14px] font-semibold text-[#0b1220] mb-2">
                    {caso.pregunta}
                  </h4>
                  <p className="text-[13.5px] text-[#6b7280] leading-[1.65]">
                    {caso.respuesta}
                  </p>
                </div>
              ))}

              {/* CTA dudas */}
              <div className="flex flex-col items-center gap-3 p-5 rounded-lg bg-blue-50 border border-blue-100 mt-2">
                <p className="text-[14px] font-semibold text-[#0b1220]">
                  ¿Tienes otra duda específica sobre tu caso?
                </p>
                <button
                  onClick={scrollToForm}
                  className="bg-white hover:bg-gray-50 text-[#0b63c6] text-[13px] font-semibold px-5 py-2.5 rounded-lg border border-blue-200 transition-colors duration-200"
                >
                  Agenda una llamada para resolverla
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Checklist Rápido ── */}
        <div
          ref={checklistRef}
          className="flex flex-col gap-6 p-8 rounded-xl bg-gradient-to-r from-[#0b1220] to-[#1e293b] border border-white/10"
        >
          <h3 className="text-[20px] font-bold text-white text-center leading-tight">
            Checklist rápido: ¿Este sistema es para ti?
          </h3>

          {/* Grid checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {checklistItems.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <CheckCircle2 size={18} strokeWidth={2} className="text-emerald-400 shrink-0" />
                <span className="text-[13.5px] text-white/90 font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* Resultado */}
          <div className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-white/15 border border-white/30">
            <p className="text-[15px] text-white/95 font-medium text-center">
              Si marcaste{' '}
              <strong className="text-emerald-400 font-bold">4 o más</strong>, este sistema es perfecto para ti.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FiltroCliente;